import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiMic, FiMicOff, FiChevronDown } from 'react-icons/fi';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { contentColumnPadding } from '../../theme/constants';
import {
  SUPPORTED_LANGUAGES,
  SAFARI_SUPPORTED_LANGUAGES,
  isSafari,
} from '../../hooks/useSpeechToText';

interface VoiceInputButtonProps {
  currentLanguage: string;
  onLanguageChange: (language: string) => void;
  isListening: boolean;
  onMicrophoneClick: () => void;
  disabled?: boolean;
  isSupported?: boolean;
}

const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({
  currentLanguage,
  onLanguageChange,
  isListening,
  onMicrophoneClick,
  disabled = false,
  isSupported = true,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const isSafariBrowser = useMemo(() => isSafari(), []);

  const allLanguagesByCountry: Record<string, string[]> = {
    '🇺🇸 United States': ['en-US'],
    '🇬🇧 United Kingdom': ['en-GB'],
    '🇮🇳 India': ['en-IN', 'hi-IN', 'bn-IN', 'ta-IN', 'te-IN', 'mr-IN', 'gu-IN', 'kn-IN', 'ml-IN', 'pa-IN'],
    '🇸🇦 Saudi Arabia': ['ar-SA'],
    '🇦🇪 UAE': ['ar-AE'],
    '🇪🇬 Egypt': ['ar-EG'],
    '🇯🇴 Jordan': ['ar-JO'],
    '🇰🇼 Kuwait': ['ar-KW'],
    '🇶🇦 Qatar': ['ar-QA'],
    '🇫🇷 France': ['fr-FR'],
    '🇩🇪 Germany': ['de-DE'],
    '🇪🇸 Spain': ['es-ES'],
    '🇮🇹 Italy': ['it-IT'],
    '🇧🇷 Brazil': ['pt-BR'],
    '🇷🇺 Russia': ['ru-RU'],
    '🇨🇳 China': ['zh-CN'],
    '🇯🇵 Japan': ['ja-JP'],
    '🇰🇷 South Korea': ['ko-KR'],
    '🇹🇷 Turkey': ['tr-TR'],
    '🇮🇩 Indonesia': ['id-ID'],
    '🇲🇾 Malaysia': ['ms-MY'],
    '🇹🇭 Thailand': ['th-TH'],
    '🇻🇳 Vietnam': ['vi-VN'],
  };

  const safariLanguagesByCountry: Record<string, string[]> = {
    '🇺🇸 United States': ['en-US'],
    '🇬🇧 United Kingdom': ['en-GB'],
    '🇮🇳 India': ['en-IN'],
  };

  const languagesByCountry = isSafariBrowser ? safariLanguagesByCountry : allLanguagesByCountry;
  const availableLanguages = isSafariBrowser ? SAFARI_SUPPORTED_LANGUAGES : SUPPORTED_LANGUAGES;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideAnchor = dropdownRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideAnchor && !insideMenu) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // The dropdown is rendered in a portal on document.body so it is not clipped
  // by the `overflow: hidden` input/page containers. Anchor it to the button
  // with fixed positioning, re-measuring on scroll/resize while open.
  useLayoutEffect(() => {
    if (!isDropdownOpen) return;

    const updatePosition = () => {
      const anchor = dropdownRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 8,
        right: window.innerWidth - rect.right,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isDropdownOpen]);

  const getShortName = (langCode: string): string => {
    const map: Record<string, string> = {
      'en-US': 'EN', 'en-GB': 'EN', 'en-IN': 'EN',
      'hi-IN': 'हिं', 'bn-IN': 'বাং', 'ta-IN': 'தமி', 'te-IN': 'తెలు',
      'mr-IN': 'मर', 'gu-IN': 'ગુ', 'kn-IN': 'ಕ', 'ml-IN': 'മ', 'pa-IN': 'ਪੰ',
      'ar-SA': 'AR', 'ar-AE': 'AR', 'ar-EG': 'AR', 'ar-JO': 'AR', 'ar-KW': 'AR', 'ar-QA': 'AR',
      'fr-FR': 'FR', 'de-DE': 'DE', 'es-ES': 'ES', 'it-IT': 'IT', 'pt-BR': 'PT',
      'ru-RU': 'RU', 'zh-CN': 'ZH', 'ja-JP': 'JA', 'ko-KR': 'KO', 'tr-TR': 'TR',
      'id-ID': 'ID', 'ms-MY': 'MS', 'th-TH': 'TH', 'vi-VN': 'VI',
    };
    return map[langCode] || langCode.split('-')[0].toUpperCase();
  };

  const tooltipText = !isSupported
    ? 'Voice input not supported. Use Chrome or Safari.'
    : isListening
    ? 'Stop recording'
    : 'Voice input';

  return (
    <Wrapper ref={dropdownRef} title={tooltipText}>
      <MicButton
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onMicrophoneClick();
        }}
        disabled={disabled || !isSupported}
        $isListening={isListening}
        data-listening={isListening}
        aria-label={isListening ? 'Stop recording' : 'Start voice input'}
      >
        {isListening ? <FiMicOff size={16} /> : <FiMic size={16} />}
        <LangBadge>{getShortName(currentLanguage)}</LangBadge>
      </MicButton>

      <ChevronButton
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled && !isListening && isSupported) {
            setIsDropdownOpen((v) => !v);
          }
        }}
        disabled={disabled || isListening || !isSupported}
        $isOpen={isDropdownOpen}
        aria-label="Select language"
      >
        <FiChevronDown size={11} />
      </ChevronButton>

      {isDropdownOpen &&
        createPortal(
          <DropdownMenu ref={menuRef} style={menuStyle}>
            <LanguageList>
              {Object.entries(languagesByCountry).map(([country, langs]) => (
                <CountryGroup key={country}>
                  <CountryHeader>{country}</CountryHeader>
                  {langs.map((code) => (
                    <LangOption
                      key={code}
                      $isSelected={code === currentLanguage}
                      onClick={() => {
                        onLanguageChange(code);
                        setIsDropdownOpen(false);
                      }}
                    >
                      <LangName>
                        {availableLanguages[code as keyof typeof availableLanguages]}
                      </LangName>
                      <LangCode>{code}</LangCode>
                    </LangOption>
                  ))}
                </CountryGroup>
              ))}
            </LanguageList>
          </DropdownMenu>,
          document.body
        )}
    </Wrapper>
  );
};

export default VoiceInputButton;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const MicButton = styled.button<{ $isListening: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: ${({ theme, $isListening }) => ($isListening ? theme.av2.error : theme.av2.surface)};
  color: ${({ theme, $isListening }) => ($isListening ? theme.av2.textOnAccent : theme.av2.textSecondary)};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${({ theme, $isListening }) => ($isListening ? theme.av2.error : theme.av2.hoverBg)};
    color: ${({ theme }) => theme.av2.textPrimary};
  }

  ${({ $isListening }) =>
    $isListening &&
    `
    animation: micPulse 1.5s infinite;
    @keyframes micPulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.75; }
    }
  `}
`;

const LangBadge = styled.span`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.av2.primary};
  color: ${({ theme }) => theme.av2.textOnAccent};
  font-size: 8px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  pointer-events: none;
`;

const ChevronButton = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 34px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.av2.textMuted};
  cursor: pointer;
  padding: 0;
  transition: color 0.12s ease;

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.av2.textSecondary};
  }

  svg {
    transition: transform 0.15s ease;
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;

const DropdownMenu = styled.div`
  position: fixed;
  width: 300px;
  max-height: 320px;
  background: ${({ theme }) => theme.av2.surfaceElevated};
  border: 1px solid ${({ theme }) => theme.av2.border};
  border-radius: 12px;
  box-shadow: 0 8px 24px ${({ theme }) => theme.av2.shadowWarm};
  z-index: 1000;
  overflow: hidden;
  animation: dropUp 0.15s ease-out;

  @keyframes dropUp {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const LanguageList = styled.div`
  overflow-y: auto;
  max-height: 320px;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.av2.border}; border-radius: 3px; }
`;

const CountryGroup = styled.div``;

const CountryHeader = styled.div`
  padding: 8px 12px 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.av2.textMuted};
  background: ${({ theme }) => theme.av2.surface};
  border-bottom: 1px solid ${({ theme }) => theme.av2.border};
  position: sticky;
  top: 0;
  z-index: 1;
`;

const LangOption = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;
  background: ${({ theme, $isSelected }) => ($isSelected ? theme.av2.primaryTint : 'transparent')};
  transition: background 0.1s ease;

  &:hover {
    background: ${({ theme, $isSelected }) =>
      $isSelected ? theme.av2.primaryTintStrong : theme.av2.hoverBg};
  }
`;

const LangName = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.av2.textPrimary};
`;

const LangCode = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.av2.textMuted};
  font-family: monospace;
`;
